import { useEffect, useId, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import './roles.css';
import { DeleteRoleOptions, RoleV2 } from '../../../types/role';

interface DeleteRoleDialogProps {
  role: RoleV2 | null;
  availableRoles: RoleV2[];
  onClose: () => void;
  onConfirm: (options?: DeleteRoleOptions) => Promise<void>;
  isSubmitting?: boolean;
}

export const DeleteRoleDialog = ({
  role,
  availableRoles,
  onClose,
  onConfirm,
  isSubmitting = false,
}: DeleteRoleDialogProps) => {
  const { t } = useTranslation();
  const reassignId = useId();
  const [reassignTo, setReassignTo] = useState<string>('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!role) {
      setReassignTo('');
      setError(undefined);
    }
  }, [role]);

  if (!role) return null;

  const needsReassign = role.userCount > 0;

  const handleConfirm = async () => {
    setError(undefined);
    try {
      if (needsReassign) {
        if (!reassignTo) {
          setError(t('dashboard.roles.delete.errors.reassignRequired'));
          return;
        }
        await onConfirm({ force: true, reassignToRoleId: reassignTo });
      } else {
        await onConfirm();
      }
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ?? (e as Error).message;
      setError(msg);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t('dashboard.roles.delete.title', { name: role.name })}
      </DialogTitle>
      <DialogContent>
        {needsReassign ? (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('dashboard.roles.delete.assignedWarning', {
                count: role.userCount,
                name: role.name,
              })}
            </Typography>
            {/* Native select: the shared MuiTextField override paints outlined
                fields dark for the mobile app, which is unreadable here. */}
            <div className="role-form-field">
              <label htmlFor={reassignId} className="role-form-label">
                {t('dashboard.roles.delete.reassignLabel')}
              </label>
              <select
                id={reassignId}
                className="filter-select-input"
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
                disabled={isSubmitting || availableRoles.length === 0}
              >
                <option value="">
                  {availableRoles.length === 0
                    ? t('dashboard.roles.delete.noTargetRoles')
                    : t('dashboard.roles.delete.reassignPlaceholder')}
                </option>
                {availableRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <Typography variant="body2">
            {t('dashboard.roles.delete.confirmText', { name: role.name })}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      {/* Plain buttons on the dashboard's own classes: the shared MUI theme is
          built for the dark mobile app and renders <Button> unreadable here. */}
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <button
          type="button"
          className="secondary-action-btn"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t('dashboard.roles.form.cancel')}
        </button>
        <button
          type="button"
          className="danger-action-btn"
          onClick={() => void handleConfirm()}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? t('dashboard.roles.delete.deleting')
            : t('dashboard.roles.delete.confirm')}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteRoleDialog;
